import { useEffect, useState, useCallback } from 'react'
import { Search, Filter, RefreshCw } from 'lucide-react'
import { useAdminUsers } from '../../hooks/useAdminUsers'
import { UserTable } from '../../components/Admin'
import { Button, Input } from '../../components/UI'

const UsersListView = () => {
  const { users, loading, hasMore, fetchUsers, loadMore } = useAdminUsers()
  const [search, setSearch] = useState('')
  const [planFilter, setPlanFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  useEffect(() => {
    fetchUsers({ search, plan: planFilter, status: statusFilter })
  }, [])

  const handleSearch = useCallback((e) => {
    e.preventDefault()
    fetchUsers({ search, plan: planFilter, status: statusFilter })
  }, [fetchUsers, search, planFilter, statusFilter])

  const handleFilterChange = useCallback((type, value) => {
    if (type === 'plan') {
      setPlanFilter(value)
      fetchUsers({ search, plan: value, status: statusFilter })
    } else if (type === 'status') {
      setStatusFilter(value)
      fetchUsers({ search, plan: planFilter, status: value })
    }
  }, [fetchUsers, search, planFilter, statusFilter])

  const handleRefresh = useCallback(() => {
    fetchUsers({ search, plan: planFilter, status: statusFilter })
  }, [fetchUsers, search, planFilter, statusFilter])

  return (
    <div className="p-8">
      <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Users</h1>
          <p className="text-dark-400 mt-1">Manage user accounts and subscriptions</p>
        </div>
        <Button variant="ghost" onClick={handleRefresh} disabled={loading}>
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Search and Filters */}
      <div className="mb-6 flex flex-col md:flex-row gap-4">
        <form onSubmit={handleSearch} className="flex-1 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400" />
            <Input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by email or name..."
              className="pl-10"
            />
          </div>
          <Button type="submit" disabled={loading}>
            Search
          </Button>
        </form>

        <div className="flex gap-2">
          <select
            value={planFilter}
            onChange={(e) => handleFilterChange('plan', e.target.value)}
            className="px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/50"
          >
            <option value="">All Plans</option>
            <option value="free">Free</option>
            <option value="basic">Basic</option>
            <option value="pro">Pro</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/50"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      <UserTable users={users} loading={loading} />

      {/* Load More */}
      {hasMore && (
        <div className="mt-6 text-center">
          <Button variant="ghost" onClick={loadMore} disabled={loading}>
            {loading ? 'Loading...' : 'Load More'}
          </Button>
        </div>
      )}

      {/* User Count */}
      {!loading && users.length > 0 && (
        <p className="mt-4 text-sm text-dark-400 text-center">
          Showing {users.length} user{users.length !== 1 ? 's' : ''}
          {hasMore && ' (more available)'}
        </p>
      )}
    </div>
  )
}

export default UsersListView
